import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import {
  ClipboardList, Filter, Loader2, FileDown, FileSpreadsheet,
  CheckCircle2, AlertTriangle, AlertOctagon, HelpCircle, Pencil, X, Save, Eye,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DatePickerInput from "../DatePickerInput";
import SearchableCombo from "./SearchableCombo";
import PerfDayReportModal from "./PerfDayReportModal";
import DictTextarea from "../DictTextarea";

/**
 * PerfRegisterView — Iter 381 / Iter 383
 *
 * Register-style transposed grid: rows = parameter (grouped by equipment),
 * columns = date. Default range = TODAY. Presets: 3d / 7d / 14d / 30d /
 * 3m / 6m / 12m. Calendar-dropdown date pickers. Inline PDF + Excel
 * export (Iter 383).
 */
const PRESETS = [
  { label: "Today",   days: 1 },
  { label: "3 days",  days: 3 },
  { label: "7 days",  days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
  { label: "3 months",  days: 90 },
  { label: "6 months",  days: 180 },
  { label: "12 months", days: 365 },
];

function todayIso() {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}
function isoMinus(days) {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off - (days - 1) * 86400e3).toISOString().slice(0, 10);
}

export default function PerfRegisterView({ site, equipmentBySite, initialFilter }) {
  const { user } = useAuth() || {};
  const role = (user?.role || "").toLowerCase();
  // Iter 381 — owner/admin/manager can edit in place (audited).
  // Iter 400.15 — portal clients can now REQUEST an edit; it sits as
  // pending until an approver in CRM (owner/admin/manager) authorises
  // it, at which point a new immutable row is appended.
  const canEdit = ["owner", "admin", "manager"].includes(role);
  const isPortal = role === "client";
  const equipment = equipmentBySite?.[site?.id] || [];

  const [preset, setPreset] = useState(1); // today
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [eqFilter, setEqFilter] = useState("");
  const [paramFilter, setParamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paramsByEq, setParamsByEq] = useState({}); // {eqId: [param, ...]}
  const [rows, setRows] = useState([]);
  // Iter 418 — bump to force /perf/history refetch after a bulk repair.
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  // Iter 400.18 — day-report modal (full log-sheet view + PDF export).
  const [dayReport, setDayReport] = useState(null); // { date: "YYYY-MM-DD" }
  const [exporting, setExporting] = useState(null);
  const highlightRef = useRef(null);
  const [highlightKey, setHighlightKey] = useState(null); // `${eqId}-${paramId}`

  // Load parameters for every equipment on the site (parallel).
  useEffect(() => {
    if (!equipment.length) return;
    (async () => {
      const out = {};
      await Promise.all(equipment.map(async (eq) => {
        try {
          const { data } = await api.get(`/perf/parameters?equipment_id=${eq.id}`);
          out[eq.id] = data;
        } catch { out[eq.id] = []; }
      }));
      setParamsByEq(out);
    })();
  }, [site?.id, equipment.length]);

  // Apply initialFilter from URL deep-link (out-of-range alerts).
  useEffect(() => {
    if (!initialFilter) return;
    if (initialFilter.equipmentId) setEqFilter(initialFilter.equipmentId);
    if (initialFilter.parameterId) setParamFilter(initialFilter.parameterId);
    if (initialFilter.date) {
      setPreset(0);
      setDateFrom(initialFilter.date);
      setDateTo(initialFilter.date);
    }
    setHighlightKey(
      initialFilter.equipmentId && initialFilter.parameterId
        ? `${initialFilter.equipmentId}-${initialFilter.parameterId}`
        : null,
    );
  }, [initialFilter?.equipmentId, initialFilter?.parameterId, initialFilter?.date]);

  const applyPreset = (days) => {
    setPreset(days);
    setDateTo(todayIso());
    setDateFrom(isoMinus(days));
  };

  // Load readings
  useEffect(() => {
    if (!site?.id) return;
    setLoading(true);
    setErr("");
    let url = `/perf/history?site_id=${site.id}&date_from=${dateFrom}&date_to=${dateTo}`;
    if (eqFilter) url += `&equipment_id=${eqFilter}`;
    if (paramFilter) url += `&parameter_id=${paramFilter}`;
    api.get(url)
      .then(({ data }) => setRows(data.rows || []))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [site?.id, dateFrom, dateTo, eqFilter, paramFilter, reloadKey]);

  // Build date column list (dense within window).
  const days = useMemo(() => {
    const out = [];
    const df = new Date(dateFrom);
    const dt = new Date(dateTo);
    const cur = new Date(df);
    while (cur <= dt) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [dateFrom, dateTo]);

  // Bucket readings: {paramId: {day: reading}}
  const byParam = useMemo(() => {
    const m = {};
    rows.filter((r) => !r.superseded_by && (!statusFilter || r.status === statusFilter))
      .forEach((r) => {
        const day = (r.reading_date || "").slice(0, 10);
        if (!m[r.parameter_id]) m[r.parameter_id] = {};
        m[r.parameter_id][day] = r;
      });
    return m;
  }, [rows, statusFilter]);

  // Equipment sections to render (all or one).
  const visibleEquipment = eqFilter
    ? equipment.filter((e) => e.id === eqFilter)
    : equipment;

  // Scroll highlighted row into view once rows have actually loaded.
  // Iter 381 hotfix — ref is null on the first pass because 36 sibling
  // registers race to fetch in parallel and layout isn't settled. Gate
  // on rows.length > 0 and defer the scrollIntoView to the next paint
  // via requestAnimationFrame so we hit the real Y coordinate.
  const scrolledFor = useRef(null);
  useEffect(() => {
    if (!highlightKey || rows.length === 0) return;
    if (scrolledFor.current === highlightKey) return;
    const raf = requestAnimationFrame(() => {
      if (!highlightRef.current) return;
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledFor.current = highlightKey;
      const el = highlightRef.current;
      el.classList.add("ring-2", "ring-[#f59e0b]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#f59e0b]"), 4000);
    });
    return () => cancelAnimationFrame(raf);
  }, [highlightKey, rows.length]);

  const bumpRow = (updated) => {
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };
  // Iter 383 — inline PDF + Excel export back on the register (spec).
  const daysWindow = Math.max(1, Math.min(365, days.length));
  const download = async (fmt) => {
    if (!site?.id) return;
    setExporting(fmt);
    try {
      // Iter 384 — pass the ACTUAL filtered window (date_from / date_to)
      // instead of a "last N days from now" cutoff, so custom filters
      // export exactly what the user sees.
      const qs = new URLSearchParams();
      if (eqFilter) qs.set("equipment_id", eqFilter);
      else qs.set("site_id", site.id);
      qs.set("date_from", dateFrom);
      qs.set("date_to", dateTo);
      qs.set("days", String(daysWindow));  // legacy fallback for older FEs
      const { data } = await api.get(`/perf/report/${fmt}?${qs.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perf-${(site?.name || "site").replace(/\s+/g, "_")}-${dateFrom}_to_${dateTo}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(`Export failed: ${formatErr(e)}`);
    } finally {
      setExporting(null);
    }
  };

  const paramById = useMemo(() => {
    const m = {};
    Object.values(paramsByEq).flat().forEach((p) => { m[p.id] = p; });
    return m;
  }, [paramsByEq]);

  return (
    <div className="hv-card overflow-hidden mb-3" data-testid={`perf-register-${site?.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 flex-wrap">
        <ClipboardList className="w-4 h-4 text-[#1fb6c1]" />
        <span className="text-sm font-medium">Register · {site?.name}</span>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-[#647087]" />}
        <span className="text-[10px] text-[#647087]">
          {days.length === 1
            ? days[0]
            : `${dateFrom} → ${dateTo} (${days.length}d)`}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {/* Iter 418 (Dirk, 2026-02-28) — Owner/Admin-only repair action.
              Heals historical rows where raw_value carries a unit
              (e.g. "1L", "45%", "3.5 bar") but `value` is null because
              a pre-Iter-415 code path silently coerced it. */}
          {canEdit && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(
                  "Backfill: scan every un-parsed raw value on this tenant (e.g. \"1L\", \"45%\") and turn them into real numbers. "
                  + "This heals the — cells you see in the Register. Continue?",
                )) return;
                try {
                  const { data } = await api.post("/perf/repair/backfill-null-value-from-raw", {});
                  toast.success(`Backfill done · healed ${data.healed} (${data.healed_from_draft || 0} from drafts) · skipped ${data.skipped_unparseable}`);
                  setReloadKey((k) => k + 1);
                } catch (e) {
                  toast.error(formatErr(e) || "Backfill failed");
                }
              }}
              className="hv-btn hv-btn-ghost text-[10px] py-1 border-amber-400/40 text-amber-300 hover:text-amber-200"
              data-testid={`perf-register-backfill-raw-${site?.id}`}
              title="Backfill missing numeric values from raw_value (heals — cells)"
            >
              <AlertTriangle className="w-3 h-3" /> Heal raw
            </button>
          )}
          <button
            type="button"
            onClick={() => download("xlsx")}
            disabled={exporting !== null}
            className="hv-btn hv-btn-ghost text-[10px] py-1 disabled:opacity-50"
            data-testid={`perf-register-export-xlsx-${site?.id}`}
            title="Export register (transposed Excel)"
          >
            <FileSpreadsheet className="w-3 h-3" /> {exporting === "xlsx" ? "…" : "Excel"}
          </button>
          <button
            type="button"
            onClick={() => download("pdf")}
            disabled={exporting !== null}
            className="hv-btn hv-btn-primary text-[10px] py-1 disabled:opacity-50"
            data-testid={`perf-register-export-pdf-${site?.id}`}
            title="Export register (HidroVerse-branded PDF)"
          >
            <FileDown className="w-3 h-3" /> {exporting === "pdf" ? "…" : "PDF"}
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2 flex-wrap text-xs">
        <Filter className="w-3 h-3 text-[#647087]" />
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => applyPreset(p.days)}
            className={`text-[10px] px-2 py-1 rounded-full border transition ${preset === p.days ? "border-[#1fb6c1] bg-[#1fb6c1]/15 text-[#1fb6c1]" : "border-white/10 text-[#a0aabf] hover:border-white/20"}`}
            data-testid={`perf-register-preset-${p.days}`}
          >
            {p.label}
          </button>
        ))}
        <span className="mx-1 text-[#647087]">│</span>
        <div className="w-[140px]">
          <DatePickerInput
            value={dateFrom}
            onChange={(iso) => { setPreset(0); setDateFrom(iso); }}
            testId={`perf-register-from-${site?.id}`}
          />
        </div>
        <span className="text-[#647087]">→</span>
        <div className="w-[140px]">
          <DatePickerInput
            value={dateTo}
            onChange={(iso) => { setPreset(0); setDateTo(iso); }}
            testId={`perf-register-to-${site?.id}`}
          />
        </div>
        <div className="w-[180px]">
          <SearchableCombo
            value={eqFilter}
            onChange={(v) => { setEqFilter(v); setParamFilter(""); }}
            options={equipment.map((eq) => ({ id: eq.id, label: eq.name }))}
            allLabel="All equipment"
            placeholder="Search equipment…"
            testId={`perf-register-eq-${site?.id}`}
          />
        </div>
        <div className="w-[220px]">
          <SearchableCombo
            value={paramFilter}
            onChange={setParamFilter}
            options={Object.entries(paramsByEq).flatMap(([eqId, ps]) =>
              ps.map((p) => ({
                id: p.id,
                label: p.name,
                group: (equipment.find((e) => e.id === eqId)?.name) || "",
              })),
            )}
            allLabel="All parameters"
            placeholder="Search parameters…"
            testId={`perf-register-param-${site?.id}`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="hv-input text-xs py-1"
          data-testid={`perf-register-status-${site?.id}`}
        >
          <option value="">Any status</option>
          <option value="ok">OK only</option>
          <option value="warn">Warn only</option>
          <option value="alarm">Alarm only</option>
        </select>
      </div>

      {err && <div className="p-3 text-sm text-rose-300">{err}</div>}

      {!equipment.length ? (
        <div className="p-6 text-center text-xs text-[#647087]">No equipment configured on this site yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
            <thead className="bg-[#0a0b0e]">
              <tr className="text-[#647087] uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2 sticky left-0 bg-[#0a0b0e] z-30 min-w-[220px] border-b border-white/5 shadow-[2px_0_0_0_rgba(255,255,255,0.05)]">Parameter</th>
                <th className="text-left px-2 py-2 whitespace-nowrap sticky left-[220px] bg-[#0a0b0e] z-30 border-b border-white/5 shadow-[2px_0_0_0_rgba(255,255,255,0.05)]">Target</th>
                {days.map((d) => (
                  <th
                    key={d}
                    className="text-center px-2 py-2 whitespace-nowrap font-mono border-b border-white/5 group"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>{d.slice(5)}</span>
                      {/* Iter 400.18 — quick access to the full day report
                          (who / when / photos / edits) + PDF export. */}
                      <button
                        type="button"
                        onClick={() => setDayReport({ date: d })}
                        className="opacity-40 hover:opacity-100 hover:text-[#22d3ee] transition p-0.5 rounded"
                        title="View full day report · photos, users, times, edits"
                        data-testid={`perf-register-day-report-${d}`}
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleEquipment.map((eq) => {
                const eqParams = (paramsByEq[eq.id] || [])
                  .filter((p) => !paramFilter || p.id === paramFilter);
                if (!eqParams.length) return null;
                return (
                  <Fragment key={eq.id}>
                    <tr className="bg-[#1fb6c1]/10 border-t-2 border-[#1fb6c1]/40">
                      <td
                        colSpan={2 + days.length}
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#1fb6c1] font-semibold sticky left-0 bg-[#0e161a] z-20 shadow-[2px_0_0_0_rgba(255,255,255,0.05)]"
                      >
                        {eq.name}
                      </td>
                    </tr>
                    {eqParams.map((p) => {
                      const isHl = highlightKey === `${eq.id}-${p.id}`;
                      return (
                        <tr
                          key={p.id}
                          ref={isHl ? highlightRef : null}
                          className={`border-t border-white/5 hover:bg-white/[0.02] transition ${isHl ? "bg-[#f59e0b]/5" : ""}`}
                          data-testid={`perf-register-row-${p.id}`}
                        >
                          <td className="px-3 py-2 sticky left-0 bg-[#121319] z-20 min-w-[220px] max-w-[220px] shadow-[2px_0_0_0_rgba(255,255,255,0.05)]">
                            <div className="font-medium text-white truncate" title={p.name}>
                              {p.name}
                              {p.unit && <span className="text-[#647087] ml-1 text-[10px]">({p.unit})</span>}
                              {p.mandatory && <span className="text-[9px] uppercase text-rose-300 ml-1">*req</span>}
                            </div>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-[10px] text-[#a0aabf] font-mono sticky left-[220px] bg-[#121319] z-20 shadow-[2px_0_0_0_rgba(255,255,255,0.05)]">
                            {targetLabel(p.target, p.unit)}
                          </td>
                          {days.map((day) => {
                            const r = byParam[p.id]?.[day];
                            if (!r) {
                              return <td key={day} className="px-2 py-2 text-center text-[#647087]">—</td>;
                            }
                            const clickable = canEdit || isPortal;
                            const isCalc = !!r.is_calculated;
                            const hasPending = !!r.pending_edit_request;
                            return (
                              <td key={day} className="px-2 py-2 text-center relative group">
                                <button
                                  type="button"
                                  onClick={() => clickable && !isCalc && setEditing(r)}
                                  disabled={!clickable || isCalc}
                                  className={`inline-flex items-center gap-1 font-mono ${clickable && !isCalc ? "hover:text-[#1fb6c1] cursor-pointer" : "cursor-default"}`}
                                  title={
                                    isCalc
                                      ? "Calculated · edit its source readings"
                                      : hasPending
                                        ? `Edit pending approval · ${r.pending_edit_request.reason || "no reason"}`
                                        : isPortal
                                          ? "Click to request an edit (photo + reason needed)"
                                          : (r.notes || "Click to edit (audited)")
                                  }
                                  data-testid={`perf-register-cell-${p.id}-${day}`}
                                >
                                  {/* Iter 418 (Dirk, 2026-02-28) — Fallback to
                                      raw_value when the numeric parse failed on
                                      old rows. Amber italic marks it visually
                                      distinct from cleanly-parsed numeric values
                                      so the operator can spot un-healed history. */}
                                  <span>
                                    {r.value ?? (r.raw_value
                                      ? <span className="italic text-amber-300" title={`Un-parsed raw value: ${r.raw_value}. Run Backfill from Repair menu to heal.`}>{r.raw_value}</span>
                                      : "—")}
                                  </span>
                                  <StatusIcon status={r.status} />
                                  {(r.edit_history || []).length > 0 && (
                                    <span className="text-[9px] text-[#a78bfa]" title={`Edited ${r.edit_history.length}× · last by ${r.edited_by_name || "?"}`}>✎</span>
                                  )}
                                  {hasPending && (
                                    <span
                                      className="text-[8px] px-1 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300"
                                      title={`Pending approval · requested by ${r.pending_edit_request.requested_by_name || "?"}`}
                                      data-testid={`perf-register-pending-${r.id}`}
                                    >
                                      pending
                                    </span>
                                  )}
                                  {r.averaged && (
                                    <span
                                      className="text-[8px] uppercase tracking-wider text-orange-300"
                                      title={`Averaged over ${r.averaged_over_days} days since ${r.averaged_from_date}`}
                                    >
                                      ~avg
                                    </span>
                                  )}
                                  {clickable && !isCalc && <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 text-[#647087]" />}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditReadingModal
          reading={editing}
          parameter={paramById[editing.parameter_id]}
          isPortal={isPortal}
          onClose={() => setEditing(null)}
          onSaved={(u) => { bumpRow(u); setEditing(null); }}
        />
      )}
      {dayReport && (
        <PerfDayReportModal
          siteId={site?.id}
          date={dayReport.date}
          onClose={() => setDayReport(null)}
        />
      )}
    </div>
  );
}

function targetLabel(t, unit) {
  if (!t || !t.kind) return "—";
  if (t.kind === "exact") return `= ${t.value ?? "?"} ${unit || ""}`;
  if (t.kind === "min")   return `≥ ${t.min ?? "?"} ${unit || ""}`;
  if (t.kind === "max")   return `≤ ${t.max ?? "?"} ${unit || ""}`;
  if (t.kind === "range") return `${t.min ?? "?"}–${t.max ?? "?"} ${unit || ""}`;
  return "—";
}

function StatusIcon({ status }) {
  const map = {
    ok:    { c: "#10b981", I: CheckCircle2 },
    warn:  { c: "#f59e0b", I: AlertTriangle },
    alarm: { c: "#f43f5e", I: AlertOctagon },
  };
  const m = map[status] || { c: "#647087", I: HelpCircle };
  return <m.I className="w-3 h-3" style={{ color: m.c }} />;
}

function EditReadingModal({ reading, parameter, isPortal, onClose, onSaved }) {
  const [value, setValue] = useState(reading.value ?? "");
  const [notes, setNotes] = useState(reading.notes || "");
  const [reason, setReason] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Iter 418.bo (Dirk, 2026-02-28) — Void flow. Same modal, extra
  // button. Clears the reading (excludes it from every "latest" +
  // completeness query) but keeps it as an audit record.

  const doVoid = async () => {
    setErr("");
    if (!reason.trim()) {
      setErr("A reason is required — this reading will disappear from the log.");
      return;
    }
    if (!window.confirm(`Void this reading?\n\nThe slot re-opens for re-capture; the original will stay on record with your reason.`)) return;
    setBusy(true);
    try {
      await api.post(`/perf/readings/${reading.id}/void`, { reason: reason.trim() });
      onSaved({ ...reading, voided_at: new Date().toISOString(), voided: true });
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setErr("");
    if (value === "" || Number.isNaN(Number(value))) {
      setErr("Enter a numeric value.");
      return;
    }
    if (!reason.trim()) {
      setErr("Reason for edit is required (audit log).");
      return;
    }
    if (isPortal && !proofFile) {
      setErr("A proof photo is required so the owner can approve.");
      return;
    }
    setBusy(true);
    try {
      if (isPortal) {
        // Iter 400.15 — portal edit-request flow. Uploads proof photo,
        // then submits the request. Owner/admin/manager get bell +
        // Approvals inbox rows.
        const fd = new FormData();
        fd.append("file", proofFile);
        fd.append("kind", "perf_edit_proof");
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const uploadId = up.data?.id;
        if (!uploadId) throw new Error("Photo upload failed.");
        await api.post("/perf/readings/edit-request", {
          reading_line_id: reading.id,
          new_value: Number(value),
          new_notes: notes || null,
          verification_photo_upload_id: uploadId,
          reason: reason.trim(),
        });
        // Optimistic — stamp a pending block so the register row shows
        // the yellow chip immediately.
        onSaved({
          ...reading,
          pending_edit_request: {
            new_value: Number(value),
            reason: reason.trim(),
            requested_by_name: "You",
            requested_at: new Date().toISOString(),
          },
        });
        return;
      }
      const { data } = await api.patch(`/perf/readings/${reading.id}`, {
        value: Number(value),
        notes: notes || null,
        reason: reason.trim(),
      });
      onSaved(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="perf-register-edit-modal">
      <div className="hv-card w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#647087]">
              {isPortal ? "Request edit · needs approval" : "Edit reading · audited"}
            </div>
            <div className="text-sm font-medium mt-0.5">
              {parameter?.name || "Reading"}
              {parameter?.unit && <span className="text-[#647087] ml-1">({parameter.unit})</span>}
            </div>
            <div className="text-[10px] text-[#647087] mt-0.5">
              {(reading.reading_date || "").slice(0, 16).replace("T", " ")} · captured by {reading.captured_by_name || "?"}
            </div>
            {isPortal && (
              <div className="text-[10px] text-amber-300 mt-1">
                Your fix will sit as <strong>pending approval</strong> until the CRM owner reviews the proof photo + reason.
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/5 text-[#a0aabf]" data-testid="perf-register-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2">{err}</div>}
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Value</div>
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="hv-input w-full font-mono"
            data-testid="perf-register-edit-value"
          />
          <div className="text-[10px] text-[#647087] mt-1">
            Previous: <span className="font-mono">{reading.value ?? "—"}</span>
          </div>
        </label>
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Notes</div>
          <DictTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="hv-input w-full text-sm resize-none"
            data-testid="perf-register-edit-notes"
          />
        </label>
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Reason for edit (required)</div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Typo — chlorine was 1.8, not 18"
            className="hv-input w-full"
            data-testid="perf-register-edit-reason"
          />
        </label>
        {isPortal && (
          <label className="block text-xs">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
              Proof photo (required)
            </div>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setProofFile(e.target.files?.[0] || null)}
              className="block w-full text-[11px] text-[#a0aabf] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[11px] file:bg-white/10 file:text-white hover:file:bg-white/20"
              data-testid="perf-register-edit-proof-photo"
            />
            {proofFile && (
              <div className="text-[10px] text-emerald-300 mt-1">
                ✓ {proofFile.name} ({(proofFile.size / 1024).toFixed(0)} kB)
              </div>
            )}
          </label>
        )}
        {(reading.edit_history || []).length > 0 && (
          <div className="text-[10px] text-[#a0aabf] border-t border-white/5 pt-2">
            <div className="uppercase tracking-wider text-[#647087] mb-1">Edit history</div>
            {(reading.edit_history || []).slice(-3).reverse().map((h, i) => (
              <div key={i} className="mb-0.5">
                <span className="font-mono text-[#a78bfa]">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                {" "}· {h.by_name || "?"} · {h.before?.value ?? "—"} → {h.after?.value ?? "—"}
                {h.reason && <span className="text-[#647087]"> · “{h.reason}”</span>}
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          {!isPortal ? (
            <button
              type="button"
              onClick={doVoid}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:text-rose-100 disabled:opacity-50"
              data-testid="perf-register-edit-void"
              title="Void this reading — clears the slot for recapture; original stays on record"
            >
              Void reading…
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="perf-register-edit-cancel">Cancel</button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="perf-register-edit-save"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {busy ? (isPortal ? "Submitting…" : "Saving…") : (isPortal ? "Submit for approval" : "Save with audit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
